import { MutationHookOptions } from '@apollo/client'
import first from 'lodash/first'

import { CROP_DIMENSION } from '@Components/Personalization/Modals/UVPrint/cropperSetup'
import {
  AddToCartLegacyProductPersonalizationInput,
  CreatePersonalizationBlobMutation,
  CreatePersonalizationBlobMutationVariables,
  Product,
} from '@Generated/graphql'
import {
  getPersonalizationsByType,
  getPersonalizationsFromProduct,
} from '@Globals/CMS/ProductHelpers'

// eslint-disable-next-line no-promise-executor-return
const wait = (delay: number) => new Promise(resolve => setTimeout(resolve, delay))

interface FetchRetryProps {
  createBlob: (options: MutationHookOptions<CreatePersonalizationBlobMutation, CreatePersonalizationBlobMutationVariables>) => Promise<any>
  cropper: Cropper
  delay?: number
  name: string
  product: Product
  tries: number
}

export const fetchRetry = async ({
  createBlob,
  cropper,
  delay = 2,
  name,
  product,
  tries,
}: FetchRetryProps): Promise<AddToCartLegacyProductPersonalizationInput | null> => {
  const contentType = 'image/jpeg'

  async function onError() {
    const triesLeft = tries - 1
    if (!triesLeft) {
      console.error('Too many retries')
      return null
    }

    await wait(delay)
    return fetchRetry({
      createBlob,
      name,
      cropper,
      tries: triesLeft,
      delay: delay * delay,
      product,
    })
  }

  const { data } = await createBlob({
    variables: {
      contentType,
      name,
    },
  })
  if (data.errors) {
    return onError()
  }
  const personalizationBlob = data.createPersonalizationBlob
  const containerInfo = cropper.getContainerData()
  const oldData = cropper.getCropBoxData()
  cropper.setCropBoxData({
    width: CROP_DIMENSION,
    height: CROP_DIMENSION,
    top: (containerInfo.height - CROP_DIMENSION) / 2,
    left: (containerInfo.width - CROP_DIMENSION) / 2,
  })
  cropper.getCroppedCanvas({
    fillColor: 'white',
  }).toBlob(async (blob) => {
    await fetch(
      `${personalizationBlob.uploadURL}`,
      {
        method: 'PUT',
        headers: { 'Content-Type': personalizationBlob.blob.contentType },
        body: blob,
      },
    )
  })

  if (oldData) {
    cropper.setCropBoxData(oldData)
  }
  const productPersonalisations = getPersonalizationsFromProduct(product)
  const personalisations = getPersonalizationsByType(productPersonalisations, 'uv-print')

  const dialogBody = document.querySelector('#dialog-wrapper-uv-print')
  if (dialogBody) {
    (dialogBody as HTMLBaseElement).style.opacity = '0'
  }
  return {
    id: first(personalisations)?.id.toString() || '',
    text: '',
    blobID: personalizationBlob.blob.id,
  }
}
